(function(angular){
	'use strict';
	
	angular
		.module('investorDashboard')
		.factory('DashboardPerformanceService', [
			'$filter',
			'$q',
			'INVESTMENT_ENTITY_ID',
			'mixpanelFactory',
			'urlFactory',
			DashboardPerformanceServiceFunction
		]);
	
	function DashboardPerformanceServiceFunction(
		$filter,
		$q,
		INVESTMENT_ENTITY_ID,
		mixpanelFactory,
		urlFactory
	) {
		var factory = {
			getPerformanceData: getPerformanceData,
			getReitPositionData: getReitPositionData,
			navigateToReitPositionView: navigateToReitPositionView,
			setActiveMobileColumn: setActiveMobileColumn,
			toggleViewFormat: toggleViewFormat,
			toggleTableSection: toggleTableSection
		};

		var maxPastYearsToShow = 2;

		return factory;

		function getPerformanceData(returnAllYears) {
			return urlFactory.apiGetPromise('/api-secured/investment-entity/' + INVESTMENT_ENTITY_ID + '/performance')
				.then(function(response) {
					return formatPerformanceData(response.data, returnAllYears);
				});
		}

		function getReitPositionData(reitId) {
			var deferred = $q.defer();

			urlFactory.apiGetPromise('/api-secured/investment-entity/' + INVESTMENT_ENTITY_ID + '/reit/' + reitId + '/position')
				.then(function(response) {
					deferred.resolve(response.data);
				})
				.catch(function() {
					deferred.resolve(undefined);
				});

			return deferred.promise;
		}

		function navigateToReitPositionView(reit) {
			urlFactory.redirect('/activity/reit-investment/' + reit.reitId + '/your-investment');
		}

		function setActiveMobileColumn(table, column) {
			table.activeMobileColumn = column;

			mixpanelFactory.track('Performance Table - Set Active Column', {
				'Table': table.name,
				'Column': column
			});
		}

		function toggleViewFormat(table, viewKey) {
			table.selectedViewFormat = viewKey;

			mixpanelFactory.track('Performance Table - Set View Format', {
				'Table': table.name,
				'Format': viewKey
			});
		}

		function toggleTableSection(table, section, expanded) {
			if(angular.isDefined(table[section])) {
				table[section].expanded = !table[section].expanded;
			} else {
				table[section] = {
					expanded: true
				};
			}

			mixpanelFactory.track('Performance Table - Toggle Section', {
				'Table': table.name,
				'Section': section,
				'Expand': expanded
			});
		}

		function formatPerformanceData(performanceData, returnAllYears) {
			if(angular.isDefined(performanceData.currentYearReturns)) {
				formatCurrentYearReturns(performanceData);
			}
			if(angular.isDefined(performanceData.priorYearReturns)) {
				formatPriorYearReturns(performanceData, returnAllYears);
			}
			if(angular.isDefined(performanceData.accountBreakdown)) {
				formatAccountValueTable(performanceData, returnAllYears);
			}

			return performanceData;
		}

		function formatAccountValueTable(performanceData, returnAllYears) {
			if(angular.isDefined(performanceData.accountBreakdown)) {
				performanceData.accountBreakdown.name = 'Account Breakdown';
				performanceData.accountBreakdown.performance = [];

				if(angular.isDefined(performanceData.accountBreakdown.currentYearPerformance)) {
					performanceData.accountBreakdown.performance.push({
						year: performanceData.accountBreakdown.currentYear + ' YTD',
						accountValuePeriod: performanceData.accountBreakdown.currentYearPerformance
					});
				}

				if(angular.isDefined(performanceData.accountBreakdown.pastYearsPerformance)) {
					performanceData.accountBreakdown.pastYearsPerformance = $filter('orderBy')(performanceData.accountBreakdown.pastYearsPerformance, 'year', false);

					var numberOfPastYears = performanceData.accountBreakdown.pastYearsPerformance.length;

					if(numberOfPastYears > maxPastYearsToShow && !returnAllYears) {
						performanceData.accountBreakdown.pastYearsPerformance = performanceData.accountBreakdown.pastYearsPerformance.slice(numberOfPastYears - maxPastYearsToShow);
						performanceData.accountBreakdown.hasAdditionalPastYearsToShow = true;
					}

					performanceData.accountBreakdown.performance = performanceData.accountBreakdown.pastYearsPerformance.concat(performanceData.accountBreakdown.performance);
				}

				performanceData.accountBreakdown.performance.push({
					year: 'All time',
					accountValuePeriod: performanceData.accountBreakdown.allTimePerformance
				});

				if(performanceData.accountBreakdown.allTimePerformance.endingValue.length > 8) {
					performanceData.smallerTableFontSize = true;
				}
			}
		}

		function formatCurrentYearReturns(performanceData) {

			var currentYearReturns = performanceData.currentYearReturns;
			currentYearReturns.name = 'Current Year Returns';

			var reitCategories = [];
			
			if(currentYearReturns.notReportingNav) {
				reitCategories.push(currentYearReturns.notReportingNav);
			}
			
			if(currentYearReturns.reportingNav) {
				reitCategories.push(currentYearReturns.reportingNav);
			}
			
			if(currentYearReturns.allFunds) {
				reitCategories.push(currentYearReturns.allFunds);
			}

			angular.forEach(reitCategories, function(category) {
				category.returns = category.returnsByQuarter;
				category.returns.yearToDate = category.yearToDateReturns;

				if(angular.isDefined(category.reits)) {
					angular.forEach(category.reits, function(reit) {
						reit.returns = reit.returnsByQuarter;
						reit.returns.yearToDate = reit.yearToDateReturns;
					});
				}
			});
		}

		function formatPriorYearReturns(performanceData, returnAllYears) {

			if(angular.isDefined(performanceData.priorYearReturns)) {
				var priorYearReturns = performanceData.priorYearReturns;
				priorYearReturns.name = 'Prior Year Returns';
				var allPeriodsWithShareholdings = priorYearReturns.allFunds.returnsByYear;
				var allFundsAllTimeReturns = priorYearReturns.allFunds.allTimeReturns;
				var maxPeriodsToShow = maxPastYearsToShow + 2; // currentYear + allTime

				allPeriodsWithShareholdings.push({
					year: 'All time',
					returns: allFundsAllTimeReturns
				});

				if(allPeriodsWithShareholdings.length > maxPeriodsToShow) {
					if(!returnAllYears) {
						var ordered = $filter('orderBy')(allPeriodsWithShareholdings, 'year');

						var spliceIndex = allPeriodsWithShareholdings.length - maxPeriodsToShow;
						ordered.splice(0, spliceIndex);
						allPeriodsWithShareholdings.splice(0, allPeriodsWithShareholdings.length);
						priorYearReturns.hasAdditionalPastYearsToShow = true;

						angular.forEach(ordered, function (orderedYear) {
							allPeriodsWithShareholdings.push(orderedYear);
						});
					}
				} else if(allPeriodsWithShareholdings.length > 0 && allPeriodsWithShareholdings.length <= maxPeriodsToShow) {
					allPeriodsWithShareholdings[0].partialYear = true;
				}

				var reitCategories = [];

				if(priorYearReturns.notReportingNav) {
					reitCategories.push(priorYearReturns.notReportingNav);
				}

				if(priorYearReturns.reportingNav) {
					reitCategories.push(priorYearReturns.reportingNav);
				}

				var flattenedList = [];

				angular.forEach(reitCategories, function(category) {

					if(angular.isDefined(category.returnsByYear)) {
						category.returnsByYear.push({
							year: 'All time',
							returns: category.allTimeReturns
						});

						flattenedList.push(category.returnsByYear);
					}

					angular.forEach(category.reits, function(reit) {

						reit.returnsByYear.push({
							year: 'All time',
							returns: reit.allTimeReturns
						});

						flattenedList.push(reit.returnsByYear);
					});
				});

				angular.forEach(flattenedList, function(listItem) {

					angular.forEach(allPeriodsWithShareholdings, function(globalYear) {

						var hasGlobalYear = false;

						angular.forEach(listItem, function(categoryYear) {

							if(categoryYear.year === globalYear.year) {
								hasGlobalYear = true;
							}
						});

						if(!hasGlobalYear) {
							listItem.push({
								year: globalYear.year
							});
						}
					});

					var maxPeriodsToShow = maxPastYearsToShow + 2; // currentYear + allTime
					var spliceIndex = listItem.length - maxPeriodsToShow;

					if(listItem.length > maxPeriodsToShow) {
						if(!returnAllYears) {
							var ordered = $filter('orderBy')(listItem, 'year');
							ordered.splice(0, spliceIndex);
							listItem.splice(0, listItem.length);

							angular.forEach(ordered, function(orderedYear) {
								listItem.push(orderedYear);
							});
						}
					}
				});
			}
		}
	}
})(window.angular);